import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { GroupedSelect } from '@components/atoms/ReactSelect/GroupedSelect';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { InvoiceTotalsSummary } from '@components/molecules/InvoiceTotalsSummary';
import {
  StandaloneInvoiceLineItemRow,
  StandaloneLineItemDraft,
} from '@components/molecules/StandaloneInvoiceLineItemRow';
import { useOrganizations } from '@hooks/api/organizations';
import {
  useCreateAndSendStandaloneInvoice,
  useStandaloneInvoiceProjects,
} from '@hooks/api/standaloneInvoice';

type DiscountMode = 'PERCENT' | 'FIXED';

interface GroupedOption {
  value: string;
  label: string;
  group: string;
}

let lineItemSeq = 0;
const emptyLineItem = (): StandaloneLineItemDraft => ({
  id: `li-${lineItemSeq++}`,
  description: '',
  quantity: 1,
  unitPrice: '',
});

const round2 = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const CURRENCY = 'USD';

// An org has no billing email; the only address is the owner's user email
// (sometimes multiple, comma-joined) — take the first as the default recipient.
const emailFromOrg = (org?: {
  ownerEmail?: string;
  ownersEmails?: string;
}): string => org?.ownerEmail || org?.ownersEmails?.split(',')[0]?.trim() || '';

const StandaloneInvoicePage: React.FC = () => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [lineItems, setLineItems] = useState<StandaloneLineItemDraft[]>([
    emptyLineItem(),
  ]);
  const [selectedOrg, setSelectedOrg] = useState<GroupedOption | null>(null);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [taxRate, setTaxRate] = useState<number | ''>('');
  const [discountMode, setDiscountMode] = useState<DiscountMode>('PERCENT');
  const [discountValue, setDiscountValue] = useState<number | ''>('');

  const { mutateAsync, isPending } = useCreateAndSendStandaloneInvoice();
  // filter 'Enabled' → status = 'ENABLED' (the default 'Active' maps to a
  // non-existent 'ACTIVE' status and returns nothing).
  const orgsQuery = useOrganizations({ limit: 1000, filter: 'Enabled' });
  const projectsQuery = useStandaloneInvoiceProjects(selectedOrg?.value);

  const orgOptions: GroupedOption[] = useMemo(
    () =>
      (orgsQuery.data?.organizations ?? []).map((org) => ({
        value: org.organizationId,
        label: org.name,
        group: org.showAsSubcontractor ? 'Subcontractors' : 'Contractors',
      })),
    [orgsQuery.data],
  );

  const projectOptions: SelectOption[] = useMemo(
    () =>
      (projectsQuery.data ?? []).map((p) => ({
        value: p.projectId,
        label: p.name,
      })),
    [projectsQuery.data],
  );
  const selectedProjectOption =
    projectOptions.find((o) => o.value === projectId) ?? null;

  // Only rows with a description are sent — build the total from the SAME set,
  // rounded per-line, matching the backend so the shown total is what's charged.
  const sendableItems = useMemo(
    () => lineItems.filter((li) => li.description.trim() !== ''),
    [lineItems],
  );

  const subtotal = useMemo(
    () =>
      round2(
        sendableItems.reduce(
          (sum, li) =>
            sum +
            round2((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0)),
          0,
        ),
      ),
    [sendableItems],
  );

  // Discount-then-tax, mirroring the backend exactly.
  const discountRate =
    discountMode === 'PERCENT' ? Number(discountValue) || 0 : 0;
  const discountFixedAmount =
    discountMode === 'FIXED' ? Number(discountValue) || 0 : 0;
  const taxRateNum = Number(taxRate) || 0;
  const discountAmount = round2(
    Math.min((subtotal * discountRate) / 100 + discountFixedAmount, subtotal),
  );
  const tax = round2(((subtotal - discountAmount) * taxRateNum) / 100);
  const total = round2(subtotal - discountAmount + tax);

  const handleSelectOrg = (option: GroupedOption | null) => {
    setSelectedOrg(option);
    setProjectId(undefined);
    const org = (orgsQuery.data?.organizations ?? []).find(
      (o) => o.organizationId === option?.value,
    );
    const email = emailFromOrg(org);
    // Only overwrite when the org actually yields an email — never blank out a
    // value the admin already typed just because the org has no owner email.
    if (email) setRecipientEmail(email);
  };

  const updateLineItem = (
    index: number,
    patch: Partial<StandaloneLineItemDraft>,
  ) => {
    setLineItems((prev) =>
      prev.map((li, i) => (i === index ? { ...li, ...patch } : li)),
    );
  };
  const addLineItem = () => setLineItems((prev) => [...prev, emptyLineItem()]);
  const removeLineItem = (index: number) =>
    setLineItems((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );

  const validate = (): string | null => {
    if (!isValidEmail(recipientEmail)) return 'Enter a valid client email';
    if (
      lineItems.some(
        (li) => li.description.trim() === '' && Number(li.unitPrice) > 0,
      )
    ) {
      return 'Add a description to every priced line item (or clear its price)';
    }
    if (sendableItems.length === 0)
      return 'Add at least one line item with a description';
    if (sendableItems.some((li) => !(Number(li.quantity) > 0)))
      return 'Every line item needs a quantity greater than zero';
    if (sendableItems.some((li) => Number(li.unitPrice) < 0))
      return 'Unit price cannot be negative';
    if (taxRateNum < 0 || taxRateNum > 100)
      return 'Tax rate must be between 0 and 100';
    if (Number(discountValue) < 0) return 'Discount cannot be negative';
    if (discountMode === 'PERCENT' && discountRate > 100)
      return 'Discount percentage cannot exceed 100';
    if (total <= 0) return 'Invoice total must be greater than zero';
    return null;
  };

  const handleSend = async () => {
    if (isPending) return;
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      const result = await mutateAsync({
        recipientEmail: recipientEmail.trim(),
        currency: CURRENCY,
        organizationId: selectedOrg?.value,
        projectId,
        taxRate: taxRateNum || undefined,
        discountRate:
          discountMode === 'PERCENT' ? discountRate || undefined : undefined,
        discountFixedAmount:
          discountMode === 'FIXED'
            ? discountFixedAmount || undefined
            : undefined,
        lineItems: sendableItems.map((li) => ({
          description: li.description.trim(),
          quantity: Number(li.quantity),
          unitPrice: Number(li.unitPrice) || 0,
        })),
      });
      toast.success(
        `Invoice sent to ${result.recipientEmail} for ${new Intl.NumberFormat(
          'en-US',
          {
            style: 'currency',
            currency: result.currency,
          },
        ).format(result.total)}`,
      );
      setRecipientEmail('');
      setLineItems([emptyLineItem()]);
      setSelectedOrg(null);
      setProjectId(undefined);
      setTaxRate('');
      setDiscountValue('');
    } catch {
      // Error toast handled in the hook's onError.
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <div className="mb-6 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10">
          <ReceiptLongOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create Invoice
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Send a standalone invoice to any client. The client receives a
            secure payment link by email.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Organization{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <GroupedSelect
              options={orgOptions}
              value={selectedOrg}
              onChange={handleSelectOrg}
              placeholder={
                orgsQuery.isLoading
                  ? 'Loading organizations…'
                  : 'Select a organization'
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Project{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <ReactSelect
              options={projectOptions}
              value={selectedProjectOption}
              isClearable
              isDisabled={!selectedOrg || projectsQuery.isLoading}
              placeholder={
                !selectedOrg
                  ? 'Select an organization first'
                  : projectsQuery.isLoading
                    ? 'Loading projects…'
                    : 'Select a project'
              }
              onChange={(value) =>
                setProjectId(
                  value ? String((value as SelectOption).value) : undefined,
                )
              }
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Client email
          </label>
          <Input
            type="email"
            placeholder="client@example.com"
            value={recipientEmail}
            disabled={isPending}
            onChange={(e) => setRecipientEmail(e.target.value)}
          />
        </div>

        <div className="mb-2 hidden grid-cols-12 gap-2 px-1 text-xs font-medium uppercase tracking-wide text-gray-400 sm:grid">
          <span className="col-span-6">Description</span>
          <span className="col-span-2">Quantity</span>
          <span className="col-span-2">Unit price</span>
          <span className="col-span-2 text-right">Amount</span>
        </div>

        {lineItems.map((item, index) => (
          <StandaloneInvoiceLineItemRow
            key={item.id}
            item={item}
            currency={CURRENCY}
            disabled={isPending}
            canRemove={lineItems.length > 1}
            onChange={(patch) => updateLineItem(index, patch)}
            onRemove={() => removeLineItem(index)}
          />
        ))}

        <button
          type="button"
          onClick={addLineItem}
          disabled={isPending}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 py-2 text-sm font-medium text-indigo-600 transition-colors hover:border-indigo-400 hover:bg-indigo-50/50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-indigo-500/10">
          <AddIcon fontSize="small" />
          Add line item
        </button>

        <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 dark:border-gray-700 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Tax rate (%)
            </label>
            <Input
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder="0"
              value={taxRate}
              disabled={isPending}
              onChange={(e) =>
                setTaxRate(e.target.value === '' ? '' : Number(e.target.value))
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Discount
            </label>
            <div className="flex items-stretch gap-2">
              <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                {(['PERCENT', 'FIXED'] as DiscountMode[]).map((mode, i) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={isPending}
                    onClick={() => setDiscountMode(mode)}
                    className={`w-10 text-sm font-semibold transition-colors ${
                      i > 0
                        ? 'border-l border-gray-300 dark:border-gray-600'
                        : ''
                    } ${
                      discountMode === mode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}>
                    {mode === 'PERCENT' ? '%' : '$'}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={0}
                step="any"
                placeholder="0"
                value={discountValue}
                disabled={isPending}
                onChange={(e) =>
                  setDiscountValue(
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t border-gray-200 pt-6 dark:border-gray-700">
          <div className="w-full max-w-xs rounded-lg bg-gray-50 p-4 dark:bg-gray-900/40">
            <InvoiceTotalsSummary
              subtotal={subtotal}
              taxRate={taxRateNum}
              tax={tax}
              discountRate={discountRate}
              discountFixedAmount={discountFixedAmount}
              discountAmount={discountAmount}
              total={total}
              currency={CURRENCY}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="primary" onClick={handleSend} disabled={isPending}>
            {isPending ? 'Sending…' : 'Send invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StandaloneInvoicePage;
